import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Logo } from "./Logo";
import { Flag, WhatsAppIcon } from "./Icons";
import { LANGS, useLang } from "../i18n/LanguageContext";
import { WHATSAPP_URL, scrollToId } from "../lib/config";

const LangSwitch = ({ className = "", suffix = "" }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center gap-1 ${className}`} role="group" aria-label="Language" data-testid={`language-switcher${suffix}`}>
      {LANGS.map((l) => {
        const active = l.code === lang;
        return (
          <button
            key={l.code}
            type="button"
            onClick={() => setLang(l.code)}
            data-testid={`lang-${l.code}${suffix}`}
            aria-pressed={active}
            title={l.label}
            className={`relative flex h-9 items-center gap-1.5 rounded-full px-2 text-xs font-semibold uppercase tracking-wide transition-colors duration-200 ${active ? "text-[var(--ink)]" : "text-[var(--mist)] hover:text-[var(--ink)]"}`}
          >
            {active && <motion.span layoutId={`lang-pill${suffix}`} className="absolute inset-0 rounded-full bg-slate-100" transition={{ type: "spring", stiffness: 420, damping: 34 }} />}
            <span className="relative flex items-center gap-1.5"><Flag code={l.flag} className="h-5 w-5" />{l.code}</span>
          </button>
        );
      })}
    </div>
  );
};

export const Header = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header id="top" className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 glass border-b border-black/5 ${scrolled ? "shadow-[0_8px_30px_-20px_rgba(11,17,32,.35)]" : ""}`} data-testid="site-header">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-3 px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-6">
          <Logo />
          <LangSwitch className="hidden lg:flex" />
        </div>
        <div className="flex items-center gap-2">
          <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="btn-wa h-10 w-10 sm:w-auto sm:px-4" data-testid="header-whatsapp-btn" aria-label={t.nav.whatsapp}>
            <WhatsAppIcon className="h-5 w-5" /><span className="hidden sm:inline text-sm">{t.nav.whatsapp}</span>
          </a>
          <button type="button" onClick={() => scrollToId("trips")} className="btn-coral h-10 px-4 text-sm sm:px-5" data-testid="header-book-btn">{t.nav.book}</button>
        </div>
      </div>
      <div className="lg:hidden border-t border-black/5">
        <div className="mx-auto flex h-11 max-w-7xl items-center overflow-x-auto px-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <LangSwitch className="w-full justify-between sm:justify-start sm:gap-2" suffix="-mobile" />
        </div>
      </div>
    </header>
  );
};
